import React, { useMemo, useState, useEffect } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";
import { isEqual } from "lodash";
import { getPreferredFormattedTimeMs } from "../../../utils/preferences";
import {
  MutateSetFullWetSession,
  MutateSetWetPeriod,
} from "../../../utils/graphql/hooks/fpAnalysisHooks";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  ChartDataLabels,
  annotationPlugin,
);

type ViewMode = "runPlans" | "tyreRuns";

const runColourMap: Record<string, string> = {
  pushLowPower: "#bdffd4",
  pushHighPower: "#ffbdfc",
  lowPower: "#bdffd4",
  highPower: "#ffbdfc",
  out: "#ffc1c1",
  slow: "#ffebb5",
  pit: "#badaff",
  unknown: "#919191",
};

const tyreColourMap: Record<number, string> = {
  1: "#667aff",
  2: "#ff6666",
  3: "#69ff66",
};

const getLapColor = (
  type: string | null,
  highPower: boolean,
  isFullWetSession: boolean,
): string => {
  if (type === null) return runColourMap.unknown;
  if (type === "Pit") return runColourMap.pit;
  if (type === "Out" || type === "In" || type === "OutIn")
    return runColourMap.out;

  // In wet session mode, show power mode colors for all flying laps
  if (isFullWetSession) {
    return highPower ? runColourMap.highPower : runColourMap.lowPower;
  }

  if (type === "Slow") return runColourMap.slow;
  if (type === "LowPowerPush" || type === "HighPowerPush") {
    return highPower ? runColourMap.pushHighPower : runColourMap.pushLowPower;
  }
  return runColourMap.pushLowPower; // default
};

ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

function DriverRunsChart() {
  const driverRunsData = useSelector(
    (state: RootState) => state.fpAnalysis.driverRuns,
    isEqual,
  );

  const sessionStatus = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.status,
    isEqual,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const flags = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.flags,
    isEqual,
  );

  const isFullWetSession = useSelector(
    (state: RootState) => state.fpAnalysis.isFullWetSession,
  );

  const highlightWetPeriod = useSelector(
    (state: RootState) => state.fpAnalysis.highlightWetPeriod,
  );

  const wetPeriodRange = useSelector(
    (state: RootState) => state.fpAnalysis.wetPeriodRange,
  );

  const setFullWetSession = MutateSetFullWetSession();
  const setWetPeriod = MutateSetWetPeriod();

  const [currentTime, setCurrentTime] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 100);
    return () => clearInterval(interval);
  }, []);

  const [viewMode, setViewMode] = useState<ViewMode>("runPlans");
  const [showCautions, setShowCautions] = useState(true);
  const [localWetRange, setLocalWetRange] = useState<[number, number] | null>(
    null,
  );

  // Clear localWetRange when subscription confirms the new value
  useEffect(() => {
    if (
      localWetRange &&
      wetPeriodRange[0] === localWetRange[0] &&
      wetPeriodRange[1] === localWetRange[1]
    ) {
      setLocalWetRange(null);
    }
  }, [wetPeriodRange, localWetRange]);

  // Use local state while dragging for immediate visual feedback
  const activeWetRange = localWetRange ?? wetPeriodRange;

  const elapsedSessionMinutes = useMemo(() => {
    const sessionStartTime = sessionStatus?.sessionStartTime;
    const stoppedMs = sessionStatus?.stoppedMilliSeconds || 0;
    const isRunning = sessionStatus?.isSessionRunning;
    const stopTime = sessionStatus?.stopTime;

    if (!sessionStartTime) return 0;

    const effectiveCurrentTime = isRunning
      ? currentTime
      : (stopTime || 0) * 1000;
    const elapsedMs = effectiveCurrentTime - sessionStartTime - stoppedMs;
    return elapsedMs / 60000;
  }, [sessionStatus, currentTime]);

  const sessionDurationMinutes = useMemo(() => {
    const finalTimeSecs = sessionStatus?.finalTime;
    if (finalTimeSecs) {
      return finalTimeSecs / 60;
    }

    const isByLaps = sessionStatus?.finalType?.includes("BY_LAPS");
    if (isByLaps && flags) {
      const chequeredFlag = Object.values(flags).find(
        (flag) => flag?.type === "FINISH",
      );
      if (chequeredFlag && typeof chequeredFlag.elapsed === "number") {
        return chequeredFlag.elapsed / 60000;
      }
    }

    return null;
  }, [sessionStatus, flags]);

  const chartData = useMemo(() => {
    if (!driverRunsData || Object.keys(driverRunsData).length === 0) {
      return {
        labels: [],
        datasets: [],
        driverColors: {} as Record<string, { bg: string; font: string }>,
      };
    }

    const drivers = Object.keys(driverRunsData).sort((a, b) => {
      const colorA = driverInfo?.[a]?.driverTeamColour || "#333333";
      const colorB = driverInfo?.[b]?.driverTeamColour || "#333333";
      return colorA.localeCompare(colorB);
    });

    const labels = drivers.map((driverKey) => {
      const info = driverInfo?.[driverKey];
      return info?.driverName || driverKey;
    });

    const driverColors: Record<string, { bg: string; font: string }> = {};
    drivers.forEach((driverKey, i) => {
      const info = driverInfo?.[driverKey];
      driverColors[labels[i]] = {
        bg: info?.driverTeamColour || "#333333",
        font: info?.driverFontColour || "#000000",
      };
    });

    const allBars: {
      x: string;
      y: [number, number];
      duration: number;
      predictedLapTime: number | null;
      type: string | null;
      startTime: number;
      color: string;
      powerModeFastestSoFarPush: string | null;
      powerModeFastestSoFarAll: string | null;
      valid: boolean;
      tyreSet: number | null;
      sided: string | null;
      frontLeft: number | null;
      frontRight: number | null;
      rearLeft: number | null;
      rearRight: number | null;
    }[] = [];

    drivers.forEach((driverKey, driverIndex) => {
      const runs = driverRunsData[driverKey];
      const driverLabel = labels[driverIndex];

      runs.forEach((run, runIndex) => {
        const isLastLap = runIndex === runs.length - 1;

        if (run.startTime < 0) {
          return;
        }

        const startMinutes = run.startTime / 60000;
        let endMinutes: number;
        let durationMs: number;

        if (run.duration === null || run.duration <= 0) {
          if (!isLastLap) {
            return;
          }
          if (elapsedSessionMinutes <= startMinutes) {
            return;
          }
          if (
            sessionDurationMinutes !== null &&
            elapsedSessionMinutes - sessionDurationMinutes >= 2 &&
            run.type !== "Pit"
          ) {
            return;
          }
          if (
            sessionDurationMinutes !== null &&
            run.type === "Pit" &&
            elapsedSessionMinutes > sessionDurationMinutes
          ) {
            endMinutes = sessionDurationMinutes;
            durationMs = (sessionDurationMinutes - startMinutes) * 60000;
          } else {
            endMinutes = elapsedSessionMinutes;
            durationMs = (elapsedSessionMinutes - startMinutes) * 60000;
          }
        } else {
          endMinutes = startMinutes + run.duration / 60000;
          durationMs = run.duration;
        }

        const tyreSet = run.tyreData?.set ?? null;
        const sided = run.tyreData?.sided ?? null;
        const isPit = run.type === "Pit";

        // In tyreRuns mode: use tyre set color, pit laps get grey
        // In runPlans mode: use the original lap type color
        let color: string;
        if (viewMode === "tyreRuns") {
          if (isPit) {
            color = "#3a3a3a";
          } else if (tyreSet) {
            color = tyreColourMap[tyreSet] || runColourMap.unknown;
          } else {
            color = runColourMap.unknown;
          }
        } else {
          // Check if lap is within wet period range
          const isInWetPeriod =
            highlightWetPeriod &&
            startMinutes >= activeWetRange[0] &&
            startMinutes <= activeWetRange[1];
          color = getLapColor(
            run.type,
            run.highPower,
            isFullWetSession || isInWetPeriod,
          );
        }

        allBars.push({
          x: driverLabel,
          y: [startMinutes, endMinutes],
          duration: durationMs,
          predictedLapTime: run.predictedLapTime ?? null,
          type: run.type,
          startTime: startMinutes,
          color,
          powerModeFastestSoFarPush: run.powerModeFastestSoFarPush,
          powerModeFastestSoFarAll: run.powerModeFastestSoFarAll,
          valid: run.valid,
          tyreSet,
          sided,
          frontLeft: run.tyreData?.frontLeft ?? null,
          frontRight: run.tyreData?.frontRight ?? null,
          rearLeft: run.tyreData?.rearLeft ?? null,
          rearRight: run.tyreData?.rearRight ?? null,
        });
      });
    });

    const isInOutPitLap = (type: string | null) =>
      type === "Out" || type === "In" || type === "OutIn" || type === "Pit";

    const getBorderColor = (bar: (typeof allBars)[0]) => {
      // In tyreRuns mode: colored borders for sided tyres
      if (viewMode === "tyreRuns") {
        if (bar.sided === "Both") return "#ffffff";
        if (bar.sided === "Front") return "#ffcc00";
        if (bar.sided === "Rear") return "#ff66ff";
        return bar.color;
      }
      // In runPlans mode: power mode and invalid highlighting
      // In wet period highlight, skip power borders; in full wet session, use All instead of Push
      const isInWetPeriod =
        highlightWetPeriod &&
        bar.startTime >= activeWetRange[0] &&
        bar.startTime <= activeWetRange[1];
      if (isInWetPeriod) {
        // Skip power borders in wet period highlight
      } else if (isFullWetSession) {
        // Use powerModeFastestSoFarAll in full wet session
        if (bar.powerModeFastestSoFarAll === "Overall") return "#a020f0";
        if (bar.powerModeFastestSoFarAll === "Driver") return "#00c853";
      } else {
        // Normal mode: use powerModeFastestSoFarPush
        if (bar.powerModeFastestSoFarPush === "Overall") return "#a020f0";
        if (bar.powerModeFastestSoFarPush === "Driver") return "#00c853";
      }
      if (!bar.valid && !isInOutPitLap(bar.type)) return "#800000";
      return bar.color;
    };

    const getBorderWidth = (bar: (typeof allBars)[0]) => {
      // In tyreRuns mode: show border for sided tyres
      if (viewMode === "tyreRuns") {
        if (bar.sided) return 4;
        return 0;
      }
      // In runPlans mode: power mode and invalid highlighting
      // In wet period highlight, skip power borders; in full wet session, use All instead of Push
      const isInWetPeriod =
        highlightWetPeriod &&
        bar.startTime >= activeWetRange[0] &&
        bar.startTime <= activeWetRange[1];
      if (isInWetPeriod) {
        // Skip power borders in wet period highlight
      } else if (isFullWetSession) {
        // Use powerModeFastestSoFarAll in full wet session
        if (bar.powerModeFastestSoFarAll) return 4;
      } else {
        // Normal mode: use powerModeFastestSoFarPush
        if (bar.powerModeFastestSoFarPush) return 4;
      }
      if (!bar.valid && !isInOutPitLap(bar.type)) return 4;
      return 0;
    };

    return {
      labels,
      driverColors,
      datasets: [
        {
          label: "Laps",
          data: allBars,
          backgroundColor: allBars.map((b) => b.color),
          borderColor: allBars.map((b) => getBorderColor(b)),
          borderWidth: allBars.map((b) => getBorderWidth(b)),
          borderRadius: 5,
          borderSkipped: false,
          barPercentage: 0.9,
          categoryPercentage: 1.0,
        },
      ],
    };
  }, [
    driverRunsData,
    driverInfo,
    elapsedSessionMinutes,
    sessionDurationMinutes,
    viewMode,
    isFullWetSession,
    highlightWetPeriod,
    activeWetRange,
  ]);

  const maxLapEndTime = useMemo(() => {
    if (!chartData.datasets[0]?.data) return 0;
    const allBars = chartData.datasets[0].data as { y: [number, number] }[];
    if (allBars.length === 0) return 0;
    return Math.max(...allBars.map((bar) => bar.y[1]));
  }, [chartData]);

  const chartMaxMinutes = useMemo(() => {
    if (sessionDurationMinutes === null) {
      return Math.max(maxLapEndTime, 45);
    }
    return Math.max(sessionDurationMinutes, maxLapEndTime);
  }, [sessionDurationMinutes, maxLapEndTime]);

  const flagPeriods = useMemo(() => {
    if (!flags) return [];

    type ChartFlag = {
      elapsed: number;
      type: "RED" | "FULL_YELLOW" | "GREEN" | "SAFETY_CAR";
    };

    const periods: { start: number; end: number; type: string }[] = [];

    const sortedFlags = Object.values(flags)
      .flatMap((flag): ChartFlag[] => {
        if (!flag || typeof flag.elapsed !== "number") return [];
        if (
          flag.type !== "RED" &&
          flag.type !== "FULL_YELLOW" &&
          flag.type !== "GREEN" &&
          flag.type !== "SAFETY_CAR"
        ) {
          return [];
        }
        if (flag.type === "GREEN" && flag.sector !== undefined) return [];
        return [{ elapsed: flag.elapsed, type: flag.type }];
      })
      .sort((a, b) => a.elapsed - b.elapsed);

    let currentFlag: { type: string; elapsed: number } | null = null;

    for (const flag of sortedFlags) {
      if (
        flag.type === "RED" ||
        flag.type === "FULL_YELLOW" ||
        flag.type === "SAFETY_CAR"
      ) {
        if (currentFlag) {
          periods.push({
            start: currentFlag.elapsed / 60000,
            end: flag.elapsed / 60000,
            type: currentFlag.type,
          });
        }
        currentFlag = { type: flag.type, elapsed: flag.elapsed };
      } else if (flag.type === "GREEN") {
        if (currentFlag) {
          periods.push({
            start: currentFlag.elapsed / 60000,
            end: flag.elapsed / 60000,
            type: currentFlag.type,
          });
          currentFlag = null;
        }
      }
    }

    if (currentFlag) {
      const endTime = Math.min(elapsedSessionMinutes, chartMaxMinutes);
      if (endTime > currentFlag.elapsed / 60000) {
        periods.push({
          start: currentFlag.elapsed / 60000,
          end: endTime,
          type: currentFlag.type,
        });
      }
    }

    return periods;
  }, [flags, elapsedSessionMinutes, chartMaxMinutes]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      plugins: {
        datalabels: {
          display: true,
          color: "#000000",
          font: { size: 12, weight: "bold" as const },
          formatter: (value: { duration: number; tyreSet: number | null }) => {
            const minDurationMs = chartMaxMinutes * 60000 * 0.015;
            if (!value?.duration || value.duration < minDurationMs) return "";
            return getPreferredFormattedTimeMs(value.duration);
          },
          anchor: "center" as const,
          align: "center" as const,
        },
        legend: {
          display: true,
          position: "bottom" as const,
          onClick: () => undefined,
          labels: {
            color: "#ffffff",
            generateLabels: () =>
              viewMode === "tyreRuns"
                ? [
                    {
                      text: "Tyre Set 1",
                      fillStyle: tyreColourMap[1],
                      strokeStyle: tyreColourMap[1],
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Tyre Set 2",
                      fillStyle: tyreColourMap[2],
                      strokeStyle: tyreColourMap[2],
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Tyre Set 3",
                      fillStyle: tyreColourMap[3],
                      strokeStyle: tyreColourMap[3],
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Pit",
                      fillStyle: "#3a3a3a",
                      strokeStyle: "#3a3a3a",
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Sided Both",
                      fillStyle: "transparent",
                      strokeStyle: "#ffffff",
                      fontColor: "#ffffff",
                      lineWidth: 3,
                    },
                    {
                      text: "Sided Front",
                      fillStyle: "transparent",
                      strokeStyle: "#ffcc00",
                      fontColor: "#ffffff",
                      lineWidth: 3,
                    },
                    {
                      text: "Sided Rear",
                      fillStyle: "transparent",
                      strokeStyle: "#ff66ff",
                      fontColor: "#ffffff",
                      lineWidth: 3,
                    },
                  ]
                : isFullWetSession
                ? [
                    {
                      text: "Low Power",
                      fillStyle: runColourMap.lowPower,
                      strokeStyle: runColourMap.lowPower,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "High Power",
                      fillStyle: runColourMap.highPower,
                      strokeStyle: runColourMap.highPower,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Out/In",
                      fillStyle: runColourMap.out,
                      strokeStyle: runColourMap.out,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Pit",
                      fillStyle: runColourMap.pit,
                      strokeStyle: runColourMap.pit,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Unknown",
                      fillStyle: runColourMap.unknown,
                      strokeStyle: runColourMap.unknown,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Invalid",
                      fillStyle: "transparent",
                      strokeStyle: "#800000",
                      fontColor: "#ffffff",
                      lineWidth: 3,
                    },
                  ]
                : [
                    {
                      text: "Push (Low Power)",
                      fillStyle: runColourMap.pushLowPower,
                      strokeStyle: runColourMap.pushLowPower,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Push (High Power)",
                      fillStyle: runColourMap.pushHighPower,
                      strokeStyle: runColourMap.pushHighPower,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Slow",
                      fillStyle: runColourMap.slow,
                      strokeStyle: runColourMap.slow,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Out/In",
                      fillStyle: runColourMap.out,
                      strokeStyle: runColourMap.out,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Pit",
                      fillStyle: runColourMap.pit,
                      strokeStyle: runColourMap.pit,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Unknown",
                      fillStyle: runColourMap.unknown,
                      strokeStyle: runColourMap.unknown,
                      fontColor: "#ffffff",
                      lineWidth: 0,
                    },
                    {
                      text: "Driver Power Fastest",
                      fillStyle: "transparent",
                      strokeStyle: "#00c853",
                      fontColor: "#ffffff",
                      lineWidth: 3,
                    },
                    {
                      text: "Overall Power Fastest",
                      fillStyle: "transparent",
                      strokeStyle: "#a020f0",
                      fontColor: "#ffffff",
                      lineWidth: 3,
                    },
                    {
                      text: "Invalid",
                      fillStyle: "transparent",
                      strokeStyle: "#800000",
                      fontColor: "#ffffff",
                      lineWidth: 3,
                    },
                  ],
          },
        },
        title: {
          display: true,
          text: viewMode === "tyreRuns" ? "Tyre Runs" : "Run Plans",
          color: "rgb(255, 255, 255)",
          font: { size: 18, weight: "bold" as const },
        },
        tooltip: {
          callbacks: {
            label: (context: TooltipItem<"bar">) => {
              const data = context.raw as {
                startTime: number;
                duration: number;
                predictedLapTime: number | null;
                type: string | null;
                tyreSet: number | null;
                sided: string | null;
                frontLeft: number | null;
                frontRight: number | null;
                rearLeft: number | null;
                rearRight: number | null;
              };
              const timeRemainingText =
                sessionDurationMinutes !== null
                  ? (() => {
                      const timeRemainingMinutes =
                        sessionDurationMinutes - data.startTime;
                      const mins = Math.floor(timeRemainingMinutes);
                      const secs = Math.round(
                        (timeRemainingMinutes - mins) * 60,
                      );
                      return `Time Remaining: ${mins}m ${secs}s`;
                    })()
                  : "";

              if (viewMode === "tyreRuns") {
                const tyreIds = `FL: ${data.frontLeft ?? "?"} | FR: ${
                  data.frontRight ?? "?"
                } | RL: ${data.rearLeft ?? "?"} | RR: ${data.rearRight ?? "?"}`;
                const sidedText = data.sided ? ` | Sided: ${data.sided}` : "";
                return `${timeRemainingText} | ${tyreIds}${sidedText}`;
              }

              const predictedText =
                data.predictedLapTime !== null
                  ? ` | Predicted: ${getPreferredFormattedTimeMs(
                      data.predictedLapTime,
                    )}`
                  : "";
              return `${timeRemainingText} | Duration: ${getPreferredFormattedTimeMs(
                data.duration,
              )}${predictedText} | Type: ${data.type || "N/A"}`;
            },
          },
        },
        annotation: {
          annotations: {
            currentTime: {
              type: "line" as const,
              yMin: elapsedSessionMinutes,
              yMax: elapsedSessionMinutes,
              borderColor: "#ffffff",
              borderWidth: 2,
              borderDash: [6, 6],
            },
            ...(sessionDurationMinutes !== null
              ? {
                  endOfSession: {
                    type: "line" as const,
                    yMin: sessionDurationMinutes,
                    yMax: sessionDurationMinutes,
                    borderColor: "#ff0000",
                    borderWidth: 2,
                    borderDash: [6, 6],
                  },
                }
              : {}),
            ...(highlightWetPeriod
              ? {
                  wetPeriodHighlight: {
                    type: "box" as const,
                    yMin: activeWetRange[0],
                    yMax: activeWetRange[1],
                    backgroundColor: "rgba(15, 30, 80, 0.5)",
                    borderWidth: 0,
                  },
                }
              : {}),
            ...flagPeriods.reduce((acc, period, index) => {
              if (!showCautions) return acc;
              acc[`flag_${index}`] = {
                type: "box" as const,
                yMin: period.start,
                yMax: period.end,
                backgroundColor:
                  period.type === "RED"
                    ? "rgba(255, 0, 0, 0.3)"
                    : "rgba(255, 255, 0, 0.3)",
                borderWidth: 0,
              };
              return acc;
            }, {} as Record<string, object>),
          },
        },
      },
      scales: {
        x: {
          type: "category" as const,
          position: "top" as const,
          labels: chartData.labels,
          title: {
            display: false,
          },
          ticks: {
            color: (context: { index: number }) => {
              const label = chartData.labels[context.index];
              return chartData.driverColors[label]?.font || "#000000";
            },
            backdropColor: (context: { index: number }) => {
              const label = chartData.labels[context.index];
              return (
                chartData.driverColors[label]?.bg || "rgba(255, 255, 255, 0.7)"
              );
            },
            showLabelBackdrop: true,
            backdropPadding: { top: 4, bottom: 1, left: 6, right: 6 },
            font: {
              size: 14,
              weight: "bold" as const,
            },
          },
          grid: { display: false },
        },
        y: {
          type: "linear" as const,
          reverse: true,
          min: 0,
          max: chartMaxMinutes,
          title: {
            display: true,
            text: "Session Time (min)",
            color: "rgb(255, 255, 255)",
          },
          ticks: {
            color: "rgb(255, 255, 255)",
            callback: (value: number | string) => {
              const num = typeof value === "number" ? value : parseFloat(value);
              return Number.isInteger(num) ? num : "";
            },
          },
          grid: { color: "rgba(255, 255, 255, 0.1)" },
        },
      },
    }),
    [
      chartData.labels,
      chartData.driverColors,
      elapsedSessionMinutes,
      sessionDurationMinutes,
      chartMaxMinutes,
      flagPeriods,
      viewMode,
      showCautions,
      isFullWetSession,
      highlightWetPeriod,
      activeWetRange,
    ],
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.toggleBox}>
        {(["runPlans", "tyreRuns"] as ViewMode[]).map((mode) => (
          <label key={mode} className={styles.radioOption}>
            <input
              type="radio"
              name="viewMode"
              value={mode}
              checked={viewMode === mode}
              onChange={() => setViewMode(mode)}
            />
            {mode === "runPlans" ? "Run Plans" : "Tyre Runs"}
          </label>
        ))}
        <div className={styles.checkboxGroup}>
          {!isFullWetSession && (
            <>
              {highlightWetPeriod && (
                <div className={styles.dualRangeSlider}>
                  <input
                    type="range"
                    className={styles.rangeSliderHorizontal}
                    min={0}
                    max={chartMaxMinutes}
                    step="any"
                    value={(localWetRange ?? wetPeriodRange)[0]}
                    onChange={(e) => {
                      const newStart = parseFloat(e.target.value);
                      const currentEnd = (localWetRange ?? wetPeriodRange)[1];
                      setLocalWetRange([
                        newStart,
                        Math.max(currentEnd, newStart),
                      ]);
                    }}
                    onMouseUp={() => {
                      if (localWetRange) {
                        setWetPeriod({
                          variables: {
                            highlightWetPeriod,
                            wetPeriodRange: localWetRange,
                          },
                        });
                      }
                    }}
                    onTouchEnd={() => {
                      if (localWetRange) {
                        setWetPeriod({
                          variables: {
                            highlightWetPeriod,
                            wetPeriodRange: localWetRange,
                          },
                        });
                      }
                    }}
                  />
                  <input
                    type="range"
                    className={styles.rangeSliderHorizontal}
                    min={0}
                    max={chartMaxMinutes}
                    step="any"
                    value={(localWetRange ?? wetPeriodRange)[1]}
                    onChange={(e) => {
                      const newEnd = parseFloat(e.target.value);
                      const currentStart = (localWetRange ?? wetPeriodRange)[0];
                      setLocalWetRange([
                        Math.min(currentStart, newEnd),
                        newEnd,
                      ]);
                    }}
                    onMouseUp={() => {
                      if (localWetRange) {
                        setWetPeriod({
                          variables: {
                            highlightWetPeriod,
                            wetPeriodRange: localWetRange,
                          },
                        });
                      }
                    }}
                    onTouchEnd={() => {
                      if (localWetRange) {
                        setWetPeriod({
                          variables: {
                            highlightWetPeriod,
                            wetPeriodRange: localWetRange,
                          },
                        });
                      }
                    }}
                  />
                </div>
              )}
              <label className={styles.radioOption}>
                <input
                  type="checkbox"
                  checked={highlightWetPeriod}
                  onChange={(e) =>
                    setWetPeriod({
                      variables: {
                        highlightWetPeriod: e.target.checked,
                        wetPeriodRange,
                      },
                    })
                  }
                />
                Highlight Wet Period
              </label>
            </>
          )}
          <label className={styles.radioOption}>
            <input
              type="checkbox"
              checked={isFullWetSession}
              onChange={(e) => {
                setFullWetSession({
                  variables: { isFullWetSession: e.target.checked },
                });
                // When enabling full wet session, disable wet period highlighting
                if (e.target.checked && highlightWetPeriod) {
                  setWetPeriod({
                    variables: {
                      highlightWetPeriod: false,
                      wetPeriodRange,
                    },
                  });
                }
              }}
            />
            Full Wet Session
          </label>
          <label className={styles.radioOption}>
            <input
              type="checkbox"
              checked={showCautions}
              onChange={(e) => setShowCautions(e.target.checked)}
            />
            Show Cautions
          </label>
        </div>
      </div>
      <div className={styles.chartBox}>
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}

export default DriverRunsChart;
